<script setup lang="ts">
/**
 * Создание проекта. Профиль типов спрашиваем сразу и один раз: он решает и
 * какие типы задач появятся, и с каких колонок начнётся доска, а поменять его
 * потом — это переразметить уже заведённые задачи.
 *
 * Ключ вводится руками и не выводится из названия: он попадёт в ключи задач и
 * в адреса, а «Разработка» → «RAZRABOTKA» никому не нужен.
 */
import { AppButton, AppModal, AppSegmentedControl, AppSwitch, AppTextField } from '@vinprop/design-system'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { V1TypeProfile } from '@/api/clients/tracker-project'
import { errorMessage } from '@/api/errors'

import type { Project } from '../api'
import { useProjectsStore } from '../store'

const props = defineProps<{ modelValue: boolean }>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [project: Project]
}>()

const projects = useProjectsStore()

const { t } = useI18n()

/** Ключ проекта: заглавная латиница, от двух до десяти букв — так его ждёт бэкенд. */
const KEY_PATTERN = /^[A-Z][A-Z0-9]{1,9}$/

const key = ref('')
const name = ref('')
const description = ref('')
const profile = ref<V1TypeProfile>('TYPE_PROFILE_DEVELOPMENT')
const isPrivate = ref(false)
const isSaving = ref(false)
const failure = ref('')

const profiles = computed(() => [
  { value: 'TYPE_PROFILE_DEVELOPMENT' as const, label: t('projects.create.profiles.development') },
  { value: 'TYPE_PROFILE_SIMPLE' as const, label: t('projects.create.profiles.simple') },
])

const profileHint = computed(() =>
  profile.value === 'TYPE_PROFILE_DEVELOPMENT'
    ? t('projects.create.profileHint.development')
    : t('projects.create.profileHint.simple'),
)

const keyError = computed(() =>
  key.value && !KEY_PATTERN.test(key.value) ? t('projects.create.errors.key') : '',
)

const canSubmit = computed(
  () => KEY_PATTERN.test(key.value) && name.value.trim().length > 0 && !isSaving.value,
)

const reset = () => {
  key.value = ''
  name.value = ''
  description.value = ''
  profile.value = 'TYPE_PROFILE_DEVELOPMENT'
  isPrivate.value = false
  failure.value = ''
}

// Форму чистим на открытии, а не на закрытии: закрыли по ошибке — вернулись и
// дописали, а не набрали заново.
watch(
  () => props.modelValue,
  (open) => {
    if (open) reset()
  },
)

const close = () => emit('update:modelValue', false)

const submit = async () => {
  if (!canSubmit.value) return

  isSaving.value = true
  failure.value = ''

  try {
    const project = await projects.create({
      key: key.value,
      name: name.value.trim(),
      description: description.value.trim(),
      typeProfile: profile.value,
      isPrivate: isPrivate.value,
    })

    emit('created', project)
    close()
  } catch (error) {
    // Ошибку показываем в самой форме и её не закрываем: занятый ключ правят
    // прямо здесь, а закрытое окно означало бы набирать всё заново.
    failure.value = errorMessage(error, t('projects.create.errors.failed'))
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <AppModal
    :model-value="modelValue"
    :title="t('projects.create.title')"
    size="small"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <form class="create-project" @submit.prevent="submit">
      <div class="create-project__row">
        <AppTextField
          v-model="key"
          class="create-project__key"
          :label="t('projects.create.key')"
          :placeholder="t('projects.create.keyPlaceholder')"
          :error="keyError"
          required
          @update:model-value="key = String($event).toUpperCase()"
        />
        <AppTextField
          v-model="name"
          class="create-project__name"
          :label="t('projects.create.name')"
          :placeholder="t('projects.create.namePlaceholder')"
          required
        />
      </div>

      <AppTextField
        v-model="description"
        :label="t('projects.create.description')"
        :placeholder="t('projects.create.descriptionPlaceholder')"
        multiline
        :rows="2"
      />

      <div class="create-project__field">
        <span class="create-project__label">{{ t('projects.create.profile') }}</span>
        <AppSegmentedControl v-model="profile" :options="profiles" size="small" />
        <span class="create-project__hint">{{ profileHint }}</span>
      </div>

      <AppSwitch v-model="isPrivate" :label="t('projects.create.private')" size="small" />
      <span class="create-project__hint">{{ t('projects.create.privateHint') }}</span>

      <p v-if="failure" class="create-project__error">{{ failure }}</p>
    </form>

    <template #footer>
      <AppButton variant="secondary" @click="close">{{ t('common.actions.cancel') }}</AppButton>
      <AppButton variant="primary" :disabled="!canSubmit" :loading="isSaving" @click="submit">
        {{ t('projects.create.submit') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
.create-project {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-project__row {
  display: flex;
  gap: 12px;
}

/* Ключ короткий и не растягивается: в нём максимум десять букв. */
.create-project__key {
  width: 120px;
  flex-shrink: 0;
}

.create-project__name {
  flex: 1;
  min-width: 0;
}

.create-project__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.create-project__label {
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.create-project__hint {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

.create-project__error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-body-sm);
}
</style>
